
import React, { useState } from 'react';
import Button from '../../../components/base/Button';

const BookingSection: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    serviceType: '',
    pickupDate: '',
    pickupTime: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d3mi9batokgsj2i80mk0', {
        method: 'POST',
        body: new URLSearchParams(formDataToSend as any),
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          phone: '',
          address: '',
          serviceType: '',
          pickupDate: '',
          pickupTime: '',
          notes: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="booking" className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Đặt lịch giặt ủi
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Điền thông tin để đặt lịch nhận đồ giặt tại nhà. Chúng tôi sẽ liên hệ xác nhận trong vòng 30 phút.
            </p>
          </div>

          <div className="bg-white rounded-2xl shadow-xl p-8">
            <form onSubmit={handleSubmit} data-readdy-form id="booking-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">
                    Họ và tên *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Nhập họ và tên của bạn"
                  />
                </div>

                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-2">
                    Số điện thoại *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Nhập số điện thoại"
                  />
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="address" className="block text-sm font-semibold text-gray-900 mb-2">
                    Địa chỉ nhận đồ *
                  </label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="Nhập địa chỉ chi tiết để nhận đồ"
                  />
                </div>

                <div>
                  <label htmlFor="serviceType" className="block text-sm font-semibold text-gray-900 mb-2">
                    Loại dịch vụ *
                  </label>
                  <select
                    id="serviceType"
                    name="serviceType"
                    value={formData.serviceType}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                  >
                    <option value="">Chọn loại dịch vụ</option>
                    <option value="Giặt ủi gia đình">Giặt ủi gia đình (15,000 VND/kg)</option>
                    <option value="Chăn mền khuyến mãi">Chăn mền khuyến mãi (10,000 VND/kg)</option>
                    <option value="Giặt đồ khách sạn">Giặt đồ khách sạn (10,000-12,000 VND/kg)</option>
                    <option value="Giặt khô cao cấp">Giặt khô cao cấp (theo loại)</option>
                    <option value="Express 3h siêu nhanh">Express 3h siêu nhanh (phụ thu 100%)</option>
                    <option value="Khác">Khác (tư vấn qua điện thoại)</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="pickupDate" className="block text-sm font-semibold text-gray-900 mb-2">
                    Ngày nhận đồ *
                  </label>
                  <input
                    type="date"
                    id="pickupDate"
                    name="pickupDate"
                    value={formData.pickupDate}
                    onChange={handleInputChange}
                    required
                    min={new Date().toISOString().split('T')[0]}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="pickupTime" className="block text-sm font-semibold text-gray-900 mb-2">
                    Giờ nhận đồ *
                  </label>
                  <select
                    id="pickupTime"
                    name="pickupTime"
                    value={formData.pickupTime}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8"
                  >
                    <option value="">Chọn khung giờ</option>
                    <option value="7:00 - 9:00">7:00 - 9:00</option>
                    <option value="9:00 - 11:00">9:00 - 11:00</option>
                    <option value="11:00 - 13:00">11:00 - 13:00</option>
                    <option value="14:00 - 16:00">14:00 - 16:00</option>
                    <option value="16:00 - 18:00">16:00 - 18:00</option>
                    <option value="18:00 - 20:00">18:00 - 20:00</option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label htmlFor="notes" className="block text-sm font-semibold text-gray-900 mb-2">
                    Ghi chú thêm
                  </label>
                  <textarea
                    id="notes"
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    rows={4}
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-none"
                    placeholder="Mô tả loại đồ cần giặt, yêu cầu đặc biệt... (tối đa 500 ký tự)"
                  />
                  <p className="text-xs text-gray-500 mt-1">{formData.notes.length}/500 ký tự</p>
                </div>
              </div>

              <div className="mt-8">
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  disabled={isSubmitting}
                  className="w-full"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Đang gửi...
                    </>
                  ) : (
                    <>
                      <i className="ri-calendar-check-line mr-2"></i>
                      Đặt lịch ngay
                    </>
                  )}
                </Button>
              </div>

              {submitStatus === 'success' && (
                <div className="mt-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                  <div className="flex items-center">
                    <i className="ri-check-circle-line mr-2"></i>
                    <span>Đặt lịch thành công! Chúng tôi sẽ liên hệ với bạn trong vòng 30 phút.</span>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mt-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                  <div className="flex items-center">
                    <i className="ri-error-warning-line mr-2"></i>
                    <span>Có lỗi xảy ra. Vui lòng thử lại hoặc gọi hotline: 0961 686 191</span>
                  </div>
                </div>
              )}
            </form>

            <div className="mt-8 pt-8 border-t border-gray-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                <div className="flex items-center justify-center">
                  <i className="ri-phone-line text-blue-600 text-xl mr-3"></i>
                  <div className="text-left">
                    <p className="font-semibold text-gray-900">Hotline</p>
                    <p className="text-blue-600">0961 686 191</p>
                  </div>
                </div>

                <div className="flex items-center justify-center">
                  <i className="ri-message-3-line text-teal-600 text-xl mr-3"></i>
                  <div className="text-left">
                    <p className="font-semibold text-gray-900">Zalo</p>
                    <p className="text-teal-600">0961 686 191</p>
                  </div>
                </div>

                <div className="flex items-center justify-center">
                  <i className="ri-time-line text-green-600 text-xl mr-3"></i>
                  <div className="text-left">
                    <p className="font-semibold text-gray-900">Phản hồi</p>
                    <p className="text-green-600">Trong 30 phút</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BookingSection;